import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../firebase";
import { slugify, countryCodeFromSlug } from "./seo";

const DEFAULT_RADIUS_KM = 25;

function mapCityDoc(id, data) {
  return {
    id,
    name: data.name,
    lat: data.lat,
    lng: data.lng,
    radiusKm: data.radius_km || DEFAULT_RADIUS_KM,
    countryCode: data.countryCode || null,
  };
}

export function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// lokasjoner_map docs are flat ({ lat, lng, geohash, active, ... }) — no
// GeoFirestore `g` field, so GeoFirestore's near() can't query it (it
// matches nothing against this shape). geofire-common isn't installed in
// this app either (only in the standalone server2 scripts), so this fetches
// the active locations once (cheap: this collection is intentionally lean)
// and filters by real distance in JS instead.
let activeLocationsPromise = null;
function fetchActiveLocations() {
  if (!activeLocationsPromise) {
    activeLocationsPromise = getDocs(query(collection(firestore, "lokasjoner_map"), where("active", "==", true))).then(
      (snapshot) =>
        snapshot.docs
          .map((doc) => doc.data())
          .filter((data) => typeof data.lat === "number" && typeof data.lng === "number"),
    );
  }
  return activeLocationsPromise;
}

// Tier 1: cities Mapli already has a doc for (created once, e.g. via the
// getLocationsAdmin "Add City" flow). No build_status/count is trusted from
// the doc itself — counts are always fetched live so "20+ steder" reflects
// whatever is actually in `lokasjoner` right now.
export async function fetchCities() {
  const snapshot = await getDocs(collection(firestore, "cities"));
  return snapshot.docs.map((docSnap) => mapCityDoc(docSnap.id, docSnap.data()));
}

// Manual curation for the landing page: create a doc at
// siteConfig/landingPage in Firebase Console with two array fields —
// `quickCities` and `featuredCities` — each a list of exact city names in
// the order they should appear. Missing/absent doc, or a name that doesn't
// match a real `cities` doc, is handled by the caller (falls back to the
// default alphabetical list).
export async function fetchLandingConfig() {
  const snap = await getDoc(doc(firestore, "siteConfig", "landingPage"));
  if (!snap.exists()) return { quickCities: [], featuredCities: [] };
  const data = snap.data();
  return {
    quickCities: Array.isArray(data.quickCities) ? data.quickCities : [],
    featuredCities: Array.isArray(data.featuredCities) ? data.featuredCities : [],
  };
}

export async function fetchCityById(id) {
  const snap = await getDoc(doc(firestore, "cities", id));
  if (!snap.exists()) return null;
  return mapCityDoc(snap.id, snap.data());
}

// For the pretty /:lang/:country/:city/:category route: resolve a city by
// its localized country slug + ASCII-folded name slug. Small collection,
// so a full fetch + client-side match is simpler than a Firestore query
// keyed on a slug field that doesn't exist on the docs.
export async function fetchCityBySlug(countrySlugValue, citySlugValue, lang) {
  const countryCode = countryCodeFromSlug(countrySlugValue, lang);
  if (!countryCode) return null;
  const all = await fetchCities();
  return all.find((c) => c.countryCode === countryCode && slugify(c.name) === citySlugValue) || null;
}

export async function countLocationsNear(city) {
  const radiusKm = city.radiusKm || DEFAULT_RADIUS_KM;
  const locations = await fetchActiveLocations();
  let count = 0;
  for (const loc of locations) {
    if (distanceKm(city.lat, city.lng, loc.lat, loc.lng) <= radiusKm) count++;
  }
  return count;
}
